import React, {useEffect} from "react";
import {connect} from "react-redux";
import {RouterState, InjectedRouter} from "react-router/lib/Router";

import Permissioner from "utils/permissioner";
import {isInitialised} from "utils/uninitialised";
import setTitle from "utils/set_title";
import MatterEditorComponent from "../components/matter_editor";
import {User} from "common/types/user";
import {bindActionCreators} from "redux";
import createMatter from "modules/matters/actions/create";
import addDocumentsToMatter from "modules/matters/actions/add_documents";
import renameMatter from "modules/matters/actions/rename";
import updateMatterContext from "modules/matters/actions/update_context";
import requestor from "requestor";
import {OrganisationId} from "common/types/organisation";
import {Project, ProjectId} from "common/types/project";
import {
  Matter,
  FileItem,
  MatterId,
  CollectionTypeNames,
} from "common/types/matter";
import refreshDcDocument from "modules/matters/actions/dc_document_refresh";
import {DocumentId} from "common/types/document";
import isMatterDocumentComplete from "../utils/is_matter_document_uploading";
import {shouldRefreshDocument} from "routes/document_list/containers/document_list";
import fetchChecklistOptions, {
  ChecklistOptions,
} from "modules/projects/actions/checklist_options_fetch";
import {useChecklistSelection} from "common_components/region_checklist_selector";

type Props = {
  user: User;
  matter: Matter;
  project: Project;
  createMatter: (
    organisationId: OrganisationId,
    projectId: ProjectId,
    name: string,
    fileItems: FileItem[],
    collectionTypeNames: CollectionTypeNames,
    context?: string,
    clientContextId?: number | null,
    isProjectContextDisabled?: boolean,
    issuesetId?: number | null,
  ) => Promise<{value: Matter}>;
  addDocumentsToMatter: (
    organisationId: OrganisationId,
    projectId: ProjectId,
    matterId: MatterId,
    fileItems: FileItem[],
    collectionTypeNames: CollectionTypeNames,
    targetParentDocumentId?: number,
    removedMsaIds?: number[],
    issuesetId?: number | null,
  ) => void;
  fetchChecklistOptions: (
    organisationId: OrganisationId,
    projectId: ProjectId,
  ) => Promise<{value: ChecklistOptions}>;
  renameMatter: (
    organisationId: OrganisationId,
    projectId: ProjectId,
    matterId: MatterId,
    name: string,
  ) => Promise<void>;
  updateMatterContext: (
    organisationId: OrganisationId,
    projectId: ProjectId,
    matterId: MatterId,
    context: string,
    clientContextId?: number | null,
    isProjectContextDisabled?: boolean,
  ) => Promise<void>;
  refreshDcDocument: (
    organisationId: OrganisationId,
    projectId: ProjectId,
    matterId: MatterId,
    documentId: DocumentId,
  ) => Promise<void>;
  router: RouterState & InjectedRouter;
  params: {
    organisationId: string;
    projectId: string;
    matterId?: string;
  };
};

function MatterEditorContainer(props: Props) {
  const matterId = parseInt(props.params.matterId ?? "", 10);
  const editingMatter = !isNaN(matterId);
  const organisationId = parseInt(props.params.organisationId, 10);
  const projectId = parseInt(props.params.projectId, 10);
  const isProjectLoaded =
    isInitialised([props.project]) && Number(props.project.id) === projectId;

  const {checklistSelector, checklistIssuesets} = useChecklistSelection({
    organisationId,
    projectId,
    isEnabled:
      isProjectLoaded && Boolean(props.project.select_checklist_on_upload),
    projectChecklistIds: isProjectLoaded ? props.project.issuesets : null,
    familyChecklistId:
      editingMatter && isInitialised([props.matter])
        ? props.matter.issueset_id ?? null
        : null,
    fetchChecklistOptions: props.fetchChecklistOptions,
  });

  const refreshDocs = docs => {
    if (matterId) {
      docs.forEach(doc =>
        props.refreshDcDocument(organisationId, projectId, matterId, doc.id),
      );
    }
  };

  useEffect(() => {
    if (!props.matter?.documents) {
      return;
    }

    const uploadingDocs = props.matter.documents.filter(
      doc => !isMatterDocumentComplete(doc),
    );
    const activeDocs = uploadingDocs.filter(shouldRefreshDocument);
    const timeout = setTimeout(() => refreshDocs(activeDocs), 10000);

    return () => clearTimeout(timeout);
  }, [props.matter?.documents]);

  const shouldRenderContainer = isInitialised(
    editingMatter ? [props.user, props.matter] : [props.user],
  );

  if (!shouldRenderContainer || !props.user.permissions) {
    return <div />;
  }

  const permissioner = new Permissioner(props.user);
  if (!permissioner.isAdmin()) {
    return permissioner.getNoPermissionMessage();
  }

  const canUploadMsow = Boolean(permissioner.hasPermission("can-upload-msow"));

  const onAnalyse = async (
    name: string,
    files: FileItem[],
    collectionTypeNames: CollectionTypeNames,
    context?: string,
    clientContextId?: number | null,
    isProjectContextDisabled?: boolean,
    targetParentDocumentId?: number,
    removedMsaIds?: number[],
  ) => {
    if (editingMatter) {
      await props.addDocumentsToMatter(
        organisationId,
        projectId,
        matterId,
        files,
        collectionTypeNames,
        targetParentDocumentId,
        removedMsaIds,
        checklistIssuesets?.[0] ?? null,
      );
    } else {
      const result = await props.createMatter(
        organisationId,
        projectId,
        name,
        files,
        collectionTypeNames,
        context,
        clientContextId,
        isProjectContextDisabled,
        checklistIssuesets?.[0] ?? null,
      );
      const newMatterId = result.value.id;
      props.router.push(
        `/organisation/${organisationId}/project/${projectId}/matter/${newMatterId}`,
      );
    }
  };

  const updateName = (name: string) => {
    if (editingMatter) {
      props.renameMatter(organisationId, projectId, matterId, name);
    }
  };

  const updateContext = (
    context: string,
    clientContextId: number | null,
    isProjectContextDisabled?: boolean,
  ) => {
    if (editingMatter) {
      props.updateMatterContext(
        organisationId,
        projectId,
        matterId,
        context,
        clientContextId,
        isProjectContextDisabled,
      );
    }
  };

  setTitle("Contractual Framework Editor");
  return (
    <MatterEditorComponent
      onAnalyse={onAnalyse}
      matter={editingMatter ? props.matter : undefined}
      updateName={updateName}
      updateContext={updateContext}
      canEditContext={Boolean(permissioner.hasPermission("can-edit-context"))}
      router={props.router}
      params={props.params}
      currentUserId={props.user.id}
      canUploadMsow={canUploadMsow}
      checklistSelector={checklistSelector}
    />
  );
}

function select(state) {
  return {
    user: state.user,
    matter: state.matter,
    project: state.project,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        createMatter: createMatter(requestor),
        addDocumentsToMatter: addDocumentsToMatter(requestor),
        renameMatter: renameMatter(requestor),
        updateMatterContext: updateMatterContext(requestor),
        refreshDcDocument: refreshDcDocument(requestor),
        fetchChecklistOptions: fetchChecklistOptions(requestor),
      },
      dispatch,
    ),
  };
}

export default connect(select, mapDispatchToProps)(MatterEditorContainer);
